import { useEffect, useId, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';

const MIN_REASON = 10;
const MAX_REASON = 500;

interface Props {
  open: boolean;
  /** Vigencia actual `AAAA-MM-DD` (la nueva debe ser distinta). */
  current: string | null;
  /** Primera fecha que admite el backend `AAAA-MM-DD` (no ofrece una que rechazaría). */
  minDate: string;
  busy?: boolean;
  /** Motivo del rechazo del backend, ya en palabras. */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (effectiveDate: string, reason: string) => void;
}

/** Reprogramación de la vigencia (G4): nueva fecha y motivo obligatorio, que queda en la
 *  trazabilidad. La fecha límite de las lecturas en el LMS se mueve con ella. */
export function RescheduleModal({
  open,
  current,
  minDate,
  busy = false,
  error = null,
  onCancel,
  onConfirm,
}: Props): ReactElement | null {
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const id = useId();
  useEffect(() => {
    if (!open) {
      setDate('');
      setReason('');
    }
  }, [open]);

  if (!open) {
    return null;
  }
  const trimmed = reason.trim();
  const ready = date !== '' && date !== current && trimmed.length >= MIN_REASON;
  return (
    <ModalShell
      open={open}
      title="Reprogramar vigencia"
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => onConfirm(date, trimmed)}
            disabled={busy || !ready}
          >
            {busy ? 'Reprogramando…' : 'Reprogramar'}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <label htmlFor={`${id}-d`} className="fld-lab">
          Nueva fecha de vigencia
        </label>
        <input
          id={`${id}-d`}
          type="date"
          value={date}
          min={minDate}
          onChange={(e) => setDate(e.target.value)}
          className="fld-full"
        />
        <label htmlFor={`${id}-r`} className="fld-lab fld-gap">
          Motivo (obligatorio)
        </label>
        <textarea
          id={`${id}-r`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={MAX_REASON}
          placeholder="Por ejemplo: el área pidió más tiempo para capacitar"
          className="fld-full"
          aria-describedby={`${id}-h`}
        />
        <p id={`${id}-h`} className="soft fld-hint">
          Al menos {MIN_REASON} caracteres ({trimmed.length}/{MAX_REASON}). La fecha límite de las
          lecturas pendientes se mueve con la vigencia.
        </p>
        {error !== null && (
          <p role="alert" className="fld-err">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
